// =====================================================================
// AgendaBulkImport — importa a agenda do médico COLANDO o texto.
// O Dr. Pablo manda a agenda em texto livre ("14/07 — São Paulo",
// "03/07 João Pessoa", "16/06 (Terça) – Balneário"...). Este diálogo
// lê cada linha, acha a DATA (DD/MM ou DD/MM/AAAA) e casa a PRAÇA pelo
// nome/cidade/chave da unidade, mostra a prévia e cria tudo de uma vez
// via /v2/admin/availability (mesmo endpoint do cadastro avulso).
//
// Só usado na aba "Agenda por data" (tenants com enable_date_agenda).
// =====================================================================
import React, { useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Chip,
  CircularProgress
} from "@material-ui/core";
import { toast } from "react-toastify";

import { createAvailability } from "../../../services/clinicAdminService";
import useIsMobile from "../../../utils/useIsMobile";

const norm = (s) =>
  String(s || "")
    .normalize("NFD")
    .split("")
    .filter((c) => {
      const code = c.charCodeAt(0);
      return code < 0x300 || code > 0x36f;
    })
    .join("")
    .toLowerCase();

// tokens significativos (>=4 chars) da unidade pra casar com o texto
const unitTokens = (u) => {
  const raw = [
    u.unit_key,
    ...norm(u.name).split(/[^a-z0-9]+/),
    ...norm(u.city || "").split(/[^a-z0-9]+/)
  ];
  return Array.from(new Set(raw.filter((t) => t && t.length >= 4)));
};

// parse do texto colado -> [{ line, date 'YYYY-MM-DD', unit|null }]
const parseAgenda = (text, units, year) => {
  const toks = units.map((u) => ({ u, tokens: unitTokens(u) }));
  const out = [];
  String(text || "")
    .split(/\r?\n/)
    .forEach((raw) => {
      const line = raw.trim();
      if (!line) return;
      const m = line.match(/(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?/);
      if (!m) return;
      const dd = m[1].padStart(2, "0");
      const mm = m[2].padStart(2, "0");
      const yy = m[3]
        ? m[3].length === 2
          ? "20" + m[3]
          : m[3]
        : String(year);
      const date = `${yy}-${mm}-${dd}`;
      const nl = norm(line);
      let unit = null;
      for (const t of toks) {
        if (t.tokens.some((tok) => nl.includes(tok))) {
          unit = t.u;
          break;
        }
      }
      out.push({ line, date, unit });
    });
  return out;
};

const brDate = (d) => String(d).slice(0, 10).split("-").reverse().join("/");

const AgendaBulkImport = ({ open, onClose, slug, units, profs, onImported }) => {
  const isMobile = useIsMobile();
  const [profId, setProfId] = useState("");
  const [year, setYear] = useState(2026);
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleParse = useCallback(() => {
    if (!units || units.length === 0) {
      toast.warn("Cadastre as unidades primeiro (aba Unidades).");
      return;
    }
    setParsed(parseAgenda(text, units, Number(year) || 2026));
  }, [text, units, year]);

  const valid = useMemo(
    () => (parsed || []).filter((p) => p.unit),
    [parsed]
  );
  const invalid = useMemo(
    () => (parsed || []).filter((p) => !p.unit),
    [parsed]
  );

  const handleImport = useCallback(async () => {
    if (!profId) {
      toast.warn("Escolha o profissional.");
      return;
    }
    if (valid.length === 0) return;
    setImporting(true);
    setProgress(0);
    let okCount = 0;
    let failCount = 0;
    for (let i = 0; i < valid.length; i += 1) {
      const p = valid[i];
      try {
        // eslint-disable-next-line no-await-in-loop
        await createAvailability(slug, {
          professional_id: profId,
          unit_id: p.unit.id,
          available_date: p.date,
          status: "open"
        });
        okCount += 1;
      } catch (e) {
        failCount += 1;
      }
      setProgress(i + 1);
    }
    setImporting(false);
    if (okCount) toast.success(`${okCount} dia(s) importado(s).`);
    if (failCount) toast.error(`${failCount} falharam (já existiam?).`);
    if (onImported) onImported();
    onClose();
  }, [valid, profId, slug, onImported, onClose]);

  const close = () => {
    if (importing) return;
    setParsed(null);
    setText("");
    setProgress(0);
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>Importar agenda (colar texto)</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" style={{ gap: 12, flexWrap: "wrap" }} mb={2}>
          <FormControl
            variant="outlined"
            size="small"
            style={{ flexGrow: 1, minWidth: 130 }}
          >
            <InputLabel>Profissional</InputLabel>
            <Select
              value={profId}
              onChange={(e) => setProfId(e.target.value)}
              label="Profissional"
            >
              {(profs || []).map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.social_name || p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            variant="outlined"
            size="small"
            type="number"
            label="Ano"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            style={{ width: 110 }}
            helperText="Pra datas DD/MM"
          />
        </Box>

        <TextField
          variant="outlined"
          fullWidth
          multiline
          minRows={6}
          maxRows={14}
          label="Cole a agenda (uma linha por dia)"
          placeholder={"03/07 João Pessoa\n07/07 Balneário\n14/07 São Paulo\n17/07 Brasília"}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />

        <Box mt={1.5} mb={1}>
          <Button variant="outlined" onClick={handleParse} disabled={importing}>
            Pré-visualizar
          </Button>
        </Box>

        {parsed && (
          <Box>
            <Typography variant="caption" color="textSecondary">
              {valid.length} dia(s) reconhecido(s)
              {invalid.length ? ` · ${invalid.length} sem praça` : ""}
            </Typography>
            <Box
              mt={1}
              style={{
                maxHeight: 220,
                overflowY: "auto",
                border: "1px solid rgba(0,0,0,0.12)",
                borderRadius: 8,
                padding: 8
              }}
            >
              {valid.map((p, i) => (
                <Box
                  key={`v${i}`}
                  display="flex"
                  alignItems="center"
                  style={{ gap: 8, padding: "3px 4px" }}
                >
                  <span style={{ minWidth: 60, fontSize: 13 }}>
                    {brDate(p.date)}
                  </span>
                  <Chip size="small" label={p.unit.name} color="primary" />
                </Box>
              ))}
              {invalid.map((p, i) => (
                <Box
                  key={`i${i}`}
                  display="flex"
                  alignItems="center"
                  style={{ gap: 8, padding: "3px 4px", opacity: 0.7 }}
                >
                  <span style={{ minWidth: 60, fontSize: 13 }}>
                    {brDate(p.date)}
                  </span>
                  <Chip
                    size="small"
                    label="praça não reconhecida"
                    variant="outlined"
                  />
                  <span style={{ fontSize: 12, color: "#999" }}>{p.line}</span>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={importing}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleImport}
          disabled={importing || !parsed || valid.length === 0}
          startIcon={
            importing ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {importing
            ? `Importando ${progress}/${valid.length}…`
            : `Importar ${valid.length} dia(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgendaBulkImport;
